import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Button, CircularProgress, Divider, Stack, Typography } from '@mui/material'
import downloadIcon from 'assets/svgs/icon_download.svg'
import BatteryRepairHeader from 'components/EVLevel/EVDetail/LiveWorkingDetail/BatteryRepairHeader'
import BatteryRepairContainer from 'components/EVLevel/EVDetail/LiveWorkingDetail/BatteryRepairContainer'
import batteryRepairsIcon from 'assets/svgs/icon_level_Live_Battery_Repair.svg'
import { ILogRepair } from 'interfaces/IBrandRepair'
import { useLazyRequestLogCertificateQuery } from 'store/api'
import { formatDate } from 'utils/formatDate'

type Props = {
  liveBatteryRepairs: ILogRepair[]
}

function LiveBatteryRepair({ liveBatteryRepairs }: Props) {
  const [getLogCertificate, { data: docData, originalArgs, isLoading }] =
    useLazyRequestLogCertificateQuery()

  const onRequestLogCertifcate = (logRepairUUID: string) => {
    getLogCertificate(logRepairUUID)
  }

  useEffect(() => {
    if (docData) {
      if (/(iPad|iPhone|iPod)/g.test(navigator.userAgent)) {
        const url = docData.data
        const xhr = new XMLHttpRequest()
        xhr.open('GET', url)
        xhr.responseType = 'blob'
        xhr.onload = function () {
          const blob = xhr.response
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = originalArgs as string
          link.click()
        }
        xhr.send()
      } else {
        window.open(docData.data, '_blank')?.focus()
      }
    }
  }, [docData])

  return (
    <Stack spacing={1}>
      {liveBatteryRepairs.map((log, idx) => (
        <Stack
          key={log.logRepairUUID}
          sx={{
            background: '#5B788F',
            color: '#FFF',
            fontSize: '14px',
          }}
        >
          {idx === 0 ? (
            <Stack
              flex={1}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ padding: '4px 8px 4px 8px' }}
            >
              <Typography sx={{ fontSize: '14px' }}>
                <FormattedMessage id="evLevel.liveBatteryRepairs.repairDate" />
              </Typography>
              <Typography sx={{ fontSize: '14px' }}>
                <FormattedMessage id="evLevel.liveBatteryRepairs.downloadCertificate" />
              </Typography>
            </Stack>
          ) : (
            <Divider sx={{ background: '#C4C4C4' }} />
          )}
          <Stack
            flex={1}
            direction="row"
            justifyContent="space-between"
            sx={{
              background: '#FFF',
              color: '#525252',
              padding: '4px 8px 4px 8px',
            }}
          >
            <Typography sx={{ fontSize: '14px', marginTop: 0.5 }}>
              {formatDate(log.repairDate)}
            </Typography>

            <Button
              onClick={() => {
                onRequestLogCertifcate(log.logRepairUUID)
              }}
            >
              {isLoading && originalArgs === log.logRepairUUID ? (
                <CircularProgress size={20} color="primary" />
              ) : (
                <img src={downloadIcon} width="20" />
              )}
            </Button>
          </Stack>
        </Stack>
      ))}
    </Stack>
  )
}

export default function LiveBatteryRepairs({ liveBatteryRepairs }: Props) {
  const [open, setOpen] = useState<boolean>(false)

  const batteryRepairsCount = liveBatteryRepairs.length

  return (
    <BatteryRepairContainer
      open={open}
      onToggleOpen={() => setOpen(!open)}
      header={
        <BatteryRepairHeader
          primaryText="Live Battery Repairs"
          courseCount={batteryRepairsCount}
          icon={batteryRepairsIcon}
          iconAlt={'Live Battery Repairs'}
        />
      }
    >
      <LiveBatteryRepair liveBatteryRepairs={liveBatteryRepairs} />
    </BatteryRepairContainer>
  )
}
